<template lang="pug">
div.neutral200(:class="isMobileScreen ? 'pt-4 px-4 pb-4' : 'pa-8'" style="min-height: calc(100dvh - 72px);")
  //- Header section
  .d-flex.align-center(:class="isMobileScreen ? 'justify-end' : 'justify-space-between'")
    .yz-heading-5.text-neutral-700(v-if="!isMobileScreen") List
    yz-dropdown(v-model="selectedWarehouseSlug" :items="warehouses" item-text="name" item-value="slug" placeholder="Select warehouse" :loading="isLoadingWarehouses" size="lg" :style="isMobileScreen ? 'width: 100%;' : 'min-width: 220px;'")

  yz-card.mt-4(outlined)
    .px-4.pt-2
      //- Desktop toolbar
      template(v-if="!isMobileScreen")
        v-row(no-gutters align="center")
          v-col
            yz-tabs(v-model="tabs" type="line" @change="onTabChanged")
              yz-tab(:label="$t('all')")
          v-col(align="end")
            .d-flex.align-center.justify-end
              yz-search-btn(v-model="searchText" @search="onSearch")
              div.ml-1
                customize-column-menu(type="hq_inventory_list" :showTooltip="false" iconSize="21")
              yz-btn.ml-3(label="Add new" size="lg" @click="onAddNew")

      //- Mobile toolbar
      template(v-else)
        .d-flex.align-center.justify-space-between.mb-2
          yz-search-btn(v-model="searchText" @search="onSearch")
          yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onAddNew")

    yz-divider
    inventory-list-filter-row.mt-4.px-4(:filters.sync="filters" ref="filter")

    //- Mobile card list view
    template(v-if="isMobileScreen")
      .px-4.mt-4
        //- Loading skeleton
        template(v-if="isFetching")
          v-card.rounded-lg.mb-3(v-for="n in 3" :key="'skeleton-' + n" flat outlined)
            .pa-4
              v-skeleton-loader(type="list-item-three-line")

        //- Empty state
        .text-center.py-8(v-else-if="!inventoryList.length")
          .yz-regular-body-sm.neutral500--text No items found

        //- Card list
        template(v-else)
          v-card.rounded-lg.mb-3(v-for="item in inventoryList" :key="item.id" flat outlined @click="onEditList(item)")
            .pa-4
              //- Header: Name + 3-dot menu
              .d-flex.align-center.justify-space-between
                .yz-medium-body-sm.neutral700--text.text-truncate.flex-grow-1(style="min-width: 0;") {{ item.name }}
                v-menu(offset-y left rounded="lg")
                  template(#activator="{ on }")
                    yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="lg" v-on="on" @click.native.stop)
                  v-list.py-0
                    v-list-item(@click="onEditList(item)")
                      v-list-item-title.yz-regular-body-sm.text-neutral-700 Edit
                    v-divider
                    v-list-item(@click="onDeleteList(item)")
                      v-list-item-title.yz-regular-body-sm.text-danger-500 Delete

              //- Created date
              .d-flex.align-center.mt-3(v-if="item.created")
                .yz-regular-body-xs.neutral500--text.mr-2 Created
                .yz-regular-body-xs.neutral700--text {{ displayDate(item.created) }}

    //- Desktop table view
    .table-scroll-wrapper(v-else :class="{ 'has-data': inventoryList.length > 0 }")
      yz-data-table.mt-2(:headers="headers" :items="inventoryList" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" no-outer-border radius="0" body-height="52px" header-height="36px" header-class="yz-bold-body-xs" header-background-color="white" disable-sort @click:row="onEditList")
        template(v-slot:item.created="{ item }")
          .yz-regular-body-sm.text-neutral-700 {{ displayDate(item.created) }}
        template(v-slot:item.actions="{ item, index }")
          div.d-flex(@click.stop)
            v-spacer
            yz-menu(offset-y :top="menuPositionTop(index)" left)
              template(#activator="{ on }")
                yz-btn(v-on="on" icon-only icon="mdi-dots-horizontal" size="lg" color="transparent")
              v-list.py-0
                v-list-item(@click="onEditList(item)")
                  v-list-item-title.yz-regular-body-sm.text-neutral-700 Edit
                v-divider
                v-list-item(@click="onDeleteList(item)")
                  v-list-item-title.yz-regular-body-sm.text-danger-500 Delete

    yz-pagination.mt-4.px-4(v-if="totalCount > pageSize" v-model="currentPage" :page-size.sync="pageSize" :count="totalCount" change-page-size arrow @input="onPageChanged")

  .dialogs
    add-edit-list-dialog(v-if="openAddEditList" :open.sync="openAddEditList" :parentId="selectedListId" :overrideCompanySlug="selectedWarehouseSlug" @updated="fetchInventoryLists" @close="selectedList = null")
    delete-list-confirmation-dialog(v-if="openDeleteListConfirmation" :open.sync="openDeleteListConfirmation" :parentData="selectedList" :overrideCompanySlug="selectedWarehouseSlug" @deleted="fetchInventoryLists")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import AddEditListDialog from '@/components/clinic/dialogs/purchasehub/AddEditListDialog.vue'
import DeleteListConfirmationDialog from '@/components/clinic/dialogs/purchasehub/DeleteListConfirmationDialog.vue'
import InventoryListFilterRow from '@/components/clinic/purchasehub/filters/InventoryListFilterRow.vue'

export default {
  name: 'InventoryList',
  components: { CustomizeColumnMenu, AddEditListDialog, DeleteListConfirmationDialog, InventoryListFilterRow },
  data: () => ({
    isFetching: false,
    isLoadingWarehouses: false,
    searchText: '',
    pageSize: 20,
    totalCount: 0,
    currentPage: 1,
    inventoryList: [],
    tabs: 0,
    selectedList: null,
    openDeleteListConfirmation: false,
    openAddEditList: false,
    filters: [],
    warehouses: [],
    selectedWarehouseSlug: null,
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      inventoryListFields: 'hq/getInventoryListFields'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    hqCompanyId () {
      return this.company?.branch_of?.id || this.company?.id
    },
    headers () {
      const fields = this.inventoryListFields.filter(item => item.enable)
      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
      })
      const actions = {
        text: '',
        value: 'actions',
        enable: true,
        sortable: false
      }
      return [...fields, actions]
    },
    selectedListId () {
      return this.selectedList ? this.selectedList.id : null
    },
  },
  watch: {
    filters () {
      this.currentPage = 1
      this.fetchInventoryLists()
    },
    selectedWarehouseSlug () {
      this.currentPage = 1
      this.fetchInventoryLists()
    },
  },
  mounted () {
    this.fetchWarehouses()
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchWarehouses () {
      this.isLoadingWarehouses = true
      try {
        const resp = await this.$axios.get(`/companies/${this.hqCompanyId}/branches`, { params: { os: 'warehouse', page_size: -1 } })
        this.warehouses = resp.data?.results || []
        if (this.warehouses.length > 0 && !this.selectedWarehouseSlug) {
          this.selectedWarehouseSlug = this.warehouses[0].slug
        }
      } catch {
        this.warehouses = []
      } finally {
        this.isLoadingWarehouses = false
      }
    },
    fetchInventoryLists () {
      if (!this.selectedWarehouseSlug) return
      this.isFetching = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage,
      }
      if (this.searchText) { params.search = this.searchText }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(`/clinics/${this.selectedWarehouseSlug}/inventory_lists`, { params })
        .then(resp => {
          this.inventoryList = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    onSearch () {
      this.currentPage = 1
      this.fetchInventoryLists()
    },
    onPageChanged () {
      this.fetchInventoryLists()
    },
    onTabChanged () {
      this.searchText = ''
      this.currentPage = 1
      this.$refs.filter.resetFilters()
    },
    onAddNew () {
      this.selectedList = null
      this.openAddEditList = true
    },
    onEditList (item) {
      this.selectedList = item
      this.openAddEditList = true
    },
    onDeleteList (item) {
      this.selectedList = item
      this.openDeleteListConfirmation = true
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.inventoryList.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
    displayDate (value) {
      if (!value) return null
      const date = DateTime.fromISO(value)
      return date.toFormat('dd MMM yyyy')
    },
  }
}
</script>

<style lang="scss" scoped>
.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
</style>
